import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	applyLineTypeUpdate,
	buildInitialState,
	createInitialSelectedFacetVal,
	initializeState,
	LineTypeUpdate,
	resetGutterLineState,
	resetUnfinalizedState,
	updateState,
} from './util';
import { EDGE_TYPES } from 'components/tool/Facet/constant';
import { CreateSpecialEdge, LinesData, UpdateLineType, UpdateSpecialEdge } from './types';
import { fetchRoofData, changePanel } from '../RoofDataSlice/roofDataSlice';
import { autoEdgeDetectionThunk } from './autoEdgeDetectionThunk';
import { appToast } from 'components/Basic/CustomToast';
const initialState: {
	data: LinesData, 
	autoEdgeDetection: {
		loading: boolean,
		error: string | null,
		enabled: boolean,
		confirmed: boolean;
	}
	prevStoredState: {
		specialEdges: SpecialEdges[];
		gutterLines: GutterLine[]
	}
} = {
	data: initializeState(),
	autoEdgeDetection: {
		loading: false,
		error: null,
		enabled: false,
		confirmed: false,
	},
	prevStoredState: {
		specialEdges: [],
		gutterLines: []
	}
};

export type EdgeToolStateType = typeof initialState;

export const roofToolLinesSlice = createSlice({
	name: 'RoofToolLinesSlice',
	initialState,
	reducers: {
		enableRoofToolLines: (state, action: PayloadAction<{ shouldEnable: boolean, initialData?: LinesData['selectedFacets'] }>) => {
			const { shouldEnable, initialData } = action.payload;
			state.autoEdgeDetection = initialState.autoEdgeDetection;
			if (!shouldEnable) {
				resetUnfinalizedState(state);
			} else {
				state.data.activeRoofTool = true;
				if (initialData) {
					state.data.selectedFacets = initialData;
				}
			}
		},
		enableEdgeLines: (state, action: PayloadAction<{ shouldEnable: boolean }>) => {
			const { shouldEnable } = action.payload;
			if (shouldEnable) {
				state.data.activeGutterLine = false;
			}
			state.data.activeEdgeTool = action.payload.shouldEnable;
			updateState(state);
		},
		enableGutterLines: (state, action: PayloadAction<{ shouldEnable: boolean }>) => {
			if (!action.payload.shouldEnable) {
				resetGutterLineState(state);
			} else {
				state.data.activeEdgeTool = false;
				state.data.activeGutterLine = true;
				state.data.addGutterLine = true;
			}
			updateState(state);
		},
		setCurrentFacet: (state, action: PayloadAction<{ currentFacetIndex: string }>) => {
			const id = action.payload.currentFacetIndex;
			if (id === state.data.currentFacetIndex) return;
			state.data.currentFacetIndex = id;
			state.data.selectedFacets[id] ??= createInitialSelectedFacetVal();
			updateState(state);
		},
		setCurrentSelectedLine: (state, action: PayloadAction<{ lineType: string }>) => {
			resetGutterLineState(state);
			const { lineType } = action.payload;
			if (lineType === state.data.currentSelectedLine) return;
			state.data.currentSelectedLine = lineType;
			updateState(state);
		},
		setHoveredLines: (state, action: PayloadAction<{ linesIndex: string[] }>) => {
			state.data.hoverLinesIndex = action.payload.linesIndex;
		},
		updateLineType: (state, action: PayloadAction<{
			updateLines: UpdateLineType[];
			createSpecialEdgesData: CreateSpecialEdge;
			updateSpecialEdgesData: UpdateSpecialEdge;
		}>) => {
			applyLineTypeUpdate(state, action.payload);
			updateState(state);
		},
		enableAddGutterLines: (state, action: PayloadAction<{ shouldEnable: boolean }>) => {
			state.data.addGutterLine = action.payload.shouldEnable;
			state.data.removeGutterLine = false;
			updateState(state);
		},
		enableRemoveGutterLines: (state, action: PayloadAction<{ shouldEnable: boolean }>) => {
			state.data.removeGutterLine = action.payload.shouldEnable;
			state.data.addGutterLine = false;
			updateState(state);
		},
		updateAddGutterLine: (state, action: PayloadAction<{
			updateLines: { id: string, coords: number[][], facetId: string, discard: boolean }[],
			// an untyped edge gets classified along with the gutter line (eaves), applied in the
			// same action so that a single undo reverts both
			edgeTypeUpdate?: LineTypeUpdate,
		}>) => {
			let shouldUpdateState = false;
			const { updateLines, edgeTypeUpdate } = action.payload;
			updateLines.map(eachLine => {
				const { id, coords, facetId, discard } = eachLine;
				state.data.selectedFacets[facetId] ??= createInitialSelectedFacetVal();
				const facet = state.data.selectedFacets[facetId];

				const doesGutterLinesExist = facet.newAddedGutterLines.findIndex(line => line.id === id);
				if (doesGutterLinesExist === -1) {
					facet.newAddedGutterLines.push({ id, coords, discard, facetId });
					shouldUpdateState = true;
				} else {
					facet.newAddedGutterLines[doesGutterLinesExist] = {
						...facet.newAddedGutterLines[doesGutterLinesExist],
						coords,
						discard
					};
					shouldUpdateState = true;
				}
			});

			if (edgeTypeUpdate) {
				applyLineTypeUpdate(state, edgeTypeUpdate);
				shouldUpdateState = true;
			}

			if (shouldUpdateState) {
				updateState(state);
			}
		},
		updateRemoveGutterLine: (state, action: PayloadAction<{ id: number | string, facetId: string }>) => {
			const { id, facetId } = action.payload;
			if (!facetId) return;
			let facet = state.data.selectedFacets[facetId];

			if(!facet){
				facet = createInitialSelectedFacetVal();

				state.data.selectedFacets[facetId] = facet;
			}

			facet.newAddedGutterLines = facet.newAddedGutterLines.filter(
				line => line.id !== id
			);

			if (
				typeof id === 'number' && !facet.removedGutterLineIds.includes(id)
			) {
				facet.removedGutterLineIds = [...facet.removedGutterLineIds, id];
			}
			console.log('Updated gutter lines:', facet.newAddedGutterLines);
			updateState(state);
		},

		updateRemoveAllGutterLine: (state, action: PayloadAction<{ id: number | string, facetId: string | null }[]>) => {
			for(const {facetId, id} of action.payload){
				if (!facetId) continue;
				let facet = state.data.selectedFacets[facetId];

				if(!facet){
					facet = createInitialSelectedFacetVal();

					state.data.selectedFacets[facetId] = facet;
				}

				facet.newAddedGutterLines = facet.newAddedGutterLines.filter(
					line => line.id !== id
				);

				if (
					typeof id === 'number' && !facet.removedGutterLineIds.includes(id)
				) {
					facet.removedGutterLineIds = [...facet.removedGutterLineIds, id];
				}

				console.log('Updated gutter lines:', facet.newAddedGutterLines);
			}
			updateState(state);
		},

		setUpateEffectedFacets:(state, action: PayloadAction<{facets: LinesData['effectedFacets']}>) => {
			state.data.effectedFacets = action.payload.facets;
		},
		undoRoofToolLines: (state) => {
			if (state.data.currentIndex > 0) {
				state.data.currentIndex -= 1;
				const snapshot = JSON.parse(state.data.linesHistory[state.data.currentIndex]);
				Object.assign(state.data, {
					...snapshot,
					linesHistory: state.data.linesHistory,
					currentIndex: state.data.currentIndex,
					activeRoofTool: state.data.activeRoofTool,
				});
			}
		},
		redoRoofToolLines: (state) => {
			if (state.data.currentIndex < state.data.linesHistory.length - 1) {
				state.data.currentIndex += 1;
				const snapshot = JSON.parse(state.data.linesHistory[state.data.currentIndex]);
				Object.assign(state.data, {
					...snapshot,
					linesHistory: state.data.linesHistory,
					currentIndex: state.data.currentIndex,
					activeRoofTool: state.data.activeRoofTool,
				});
			}
		},
		resetEdgeEdits: (state) => {
			state.data.createSpecialEdges = [];
			state.data.updateSpecialEdges = [];
			state.data.currentIndex = 0;
			state.data.linesHistory = [];
			state.data.currentFacetIndex = null;
			state.data.currentSelectedLine = null;
		},
		removeAutoDetected:(state) => {
			// special edges
			for(const facetId in state.data.selectedFacets){
				const selectedLines = state.data.selectedFacets[facetId].selectedLines;
				const gutterLines = state.data.selectedFacets[facetId].gutterLines;
				state.data.selectedFacets[facetId].selectedLines = selectedLines.filter(l=>!l.isSystemDetected);
				state.data.selectedFacets[facetId].gutterLines = gutterLines.filter(l=>	l.userMapped);
			}
		},
		resetSpecialEdgesAndGutterLines: (state, action: PayloadAction<{ 
			specialEdges: SpecialEdges[];
			gutterLines: GutterLine[] ,
		}>) => {

			for(const specialEdge of action.payload.specialEdges){
				const edgesToUpdate = [];
				if(specialEdge){
					for(const roofSegmentId in specialEdge.edges){
						const edgeId = specialEdge.edges[roofSegmentId];
						edgesToUpdate.push({
							roofSegmentEdgeId: specialEdge.edges[roofSegmentId],
							roofSegmentId: roofSegmentId,
							discard: false
						});
						const selectedFacet = state.data.selectedFacets[roofSegmentId];
						selectedFacet.selectedLines = selectedFacet.selectedLines.map(l=>{
							if(l.id === edgeId){
								l.lineType= '';
							}
							return l;
						});
					}
				}

				state.data.updateSpecialEdges = state.data.updateSpecialEdges.concat([{
					specialEdgeId: specialEdge.id,
					edgeType: EDGE_TYPES.NONE,
					edges: edgesToUpdate,
				}]);
				
			}

			for(const gl of action.payload.gutterLines){
				if(!gl.roofSegmentId) continue;
				if(gl.roofSegmentId in state.data.selectedFacets){
					const facet = state.data.selectedFacets[gl.roofSegmentId];
					state.data.selectedFacets[gl.roofSegmentId].gutterLines = facet.gutterLines
						.filter(existing => existing.id !== gl.id);
					state.data.selectedFacets[gl.roofSegmentId].removedGutterLineIds = [...facet.removedGutterLineIds, gl.id];
				}
			}
			
			// state.data.updateSpecialEdges=
		},
		setAutoEdgeDetectedConfirmed: (state, action:PayloadAction<boolean>)=>{
			state.autoEdgeDetection.enabled =false;
			state.autoEdgeDetection.confirmed= action.payload;
		},

	},
	extraReducers: (builder) => {
		builder.addCase(fetchRoofData.fulfilled, (state, action) => {
			state.data = initializeState();
			state.prevStoredState = {
				specialEdges: action.payload.roofData.specialEdges,
				gutterLines: action.payload.roofData.allGutterLines
			};
		}).addCase(changePanel.fulfilled, (state, action) => {
			if (action.meta.arg.switchingSource) {
				state.data = initializeState();
			}
			state.prevStoredState = {
				specialEdges: action.payload.roofData.specialEdges,
				gutterLines: action.payload.roofData.allGutterLines
			};
		})
			.addCase(autoEdgeDetectionThunk.pending, (state) => {
				state.autoEdgeDetection.loading = true;
				state.autoEdgeDetection.error = null;
			})
			.addCase(autoEdgeDetectionThunk.fulfilled, (state, action) => {
				state.autoEdgeDetection.loading = false;
				state.autoEdgeDetection.enabled = true;
				const initData = buildInitialState(
					action.payload.specialEdges,
					action.payload.gutterLines
				);
				// state.data.createSpecialEdges = initData.draftSpecialEdges;
				state.data.selectedFacets = initData.selectedFacets;
				state.data.createSpecialEdges = [];
				state.data.updateSpecialEdges =[];
				state.data.linesHistory = [];
				state.data.currentIndex = 0;
			})
			.addCase(autoEdgeDetectionThunk.rejected, (state, action) => {
				state.autoEdgeDetection.loading = false;
				state.autoEdgeDetection.error = action.payload ?? 'Something went wrong';
				appToast.error(action.payload ?? 'Something went wrong');
			});
	}
});

export const {
	enableRoofToolLines,
	enableEdgeLines,
	enableGutterLines,
	setCurrentFacet,
	setCurrentSelectedLine,
	setHoveredLines,
	updateLineType,
	enableAddGutterLines,
	enableRemoveGutterLines,
	updateAddGutterLine,
	updateRemoveGutterLine,
	undoRoofToolLines,
	redoRoofToolLines,
	setUpateEffectedFacets,
	updateRemoveAllGutterLine,
	resetEdgeEdits,
	removeAutoDetected,
	setAutoEdgeDetectedConfirmed,
	resetSpecialEdgesAndGutterLines,
} = roofToolLinesSlice.actions;

export default roofToolLinesSlice.reducer;